import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import StatusBadge from "../components/StatusBadge";

const EXTRACTION_LABEL = {
    PENDING: { label: "Pending extraction", cls: "bg-neutral-100 text-neutral-600" },
    RUNNING: { label: "Extracting…", cls: "bg-amber-50 text-amber-700" },
    READY: { label: "Ready for review", cls: "bg-blue-50 text-blue-700" },
    FAILED: { label: "Extraction failed", cls: "bg-red-50 text-red-700" },
};

export default function Reviews() {
    const nav = useNavigate();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/documents", { params: { status: "NEEDS_REVIEW" } });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 4000);
        return () => clearInterval(t);
    }, []);

    return (
        <div data-testid="reviews-page" className="space-y-8">
            <div>
                <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                    Knowledge
                </div>
                <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                    Reviews
                </h1>
                <p className="mt-1.5 text-sm text-neutral-500">
                    AI has enriched every step and image. Verify each finding before a
                    document can move into training production.
                </p>
            </div>
            <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Title</TableHead>
                            <TableHead>Revision</TableHead>
                            <TableHead>Extraction</TableHead>
                            <TableHead className="text-right">Sections</TableHead>
                            <TableHead className="text-right">Steps</TableHead>
                            <TableHead className="text-right">Images</TableHead>
                            <TableHead>Status</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody data-testid="reviews-table-body">
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center text-sm text-neutral-500">
                                    Loading…
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} className="py-12 text-center text-sm text-neutral-500">
                                    Nothing to review yet — upload a document to get started.
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((r) => {
                            const ex = EXTRACTION_LABEL[r.extraction_status || "PENDING"] || EXTRACTION_LABEL.PENDING;
                            const disabled = r.extraction_status !== "READY";
                            return (
                                <TableRow
                                    key={r.id}
                                    data-testid={`review-row-${r.id}`}
                                    onClick={() => !disabled && nav(`/knowledge/reviews/${r.id}`)}
                                    className={disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:bg-neutral-50"}
                                >
                                    <TableCell>
                                        <div className="font-medium text-neutral-900">
                                            {r.parsed_title || r.original_filename}
                                        </div>
                                        <div className="mono text-[11px] text-neutral-500">
                                            {r.parsed_doc_number || r.original_filename}
                                        </div>
                                    </TableCell>
                                    <TableCell className="mono text-xs text-neutral-600">
                                        {r.parsed_revision || "—"}
                                    </TableCell>
                                    <TableCell>
                                        <span className={`inline-flex items-center rounded-md border border-neutral-200 px-2 py-0.5 text-[11px] font-medium ${ex.cls}`}>
                                            {ex.label}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right mono text-sm">{r.section_count}</TableCell>
                                    <TableCell className="text-right mono text-sm">{r.step_count}</TableCell>
                                    <TableCell className="text-right mono text-sm">{r.media_count}</TableCell>
                                    <TableCell>
                                        <StatusBadge status={r.status} />
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
